import React, { useState, useCallback, useEffect } from 'react'
import { XMarkIcon, DocumentTextIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { falabellaInvoiceService, FalabellaInvoiceUploadRequest } from '../../api/falabellaInvoiceService'
import { toast } from 'react-hot-toast'
import LoadingSpinner from '../LoadingSpinner'

interface FalabellaInvoiceUploadModalProps {
  isOpen: boolean
  onClose: () => void
  ordersId: string
  orderNumber?: string
}

const FalabellaInvoiceUploadModal: React.FC<FalabellaInvoiceUploadModalProps> = ({
  isOpen,
  onClose,
  ordersId,
  orderNumber
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasSuccessfulLoad, setHasSuccessfulLoad] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invoiceInfo, setInvoiceInfo] = useState<any>(null) // Reservado para uso futuro
  const [formData, setFormData] = useState<FalabellaInvoiceUploadRequest>({
    orderItemIds: [ordersId],
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    invoiceType: 'FACTURA',
    operatorCode: 'FACO',
    invoiceDocumentFormat: 'pdf',
    invoiceDocument: ''
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleGetInvoiceInfo = useCallback(async () => {
    if (!ordersId.trim()) {
      toast.error('ID de pedido es requerido')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await falabellaInvoiceService.getInvoiceInfo()

      if (response.success && response.data) {
        setInvoiceInfo(response.data)
        setHasSuccessfulLoad(true)
        if (!isInitialLoad) {
         // toast.success('Información de facturas obtenida exitosamente')
        }
      } else {
        setError(response.message || 'No se pudo obtener la información de facturas')
        setHasSuccessfulLoad(false)
        if (!isInitialLoad) {
          toast.error(response.message || 'Error al obtener la información')
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al consultar la información de facturas'
      setError(errorMessage)
      setHasSuccessfulLoad(false)
      if (!isInitialLoad) {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
      setIsInitialLoad(false)
    }
  }, [ordersId, isInitialLoad])

  // Consultar automáticamente al abrir el modal
  useEffect(() => {
    if (isOpen && ordersId.trim()) {
      handleGetInvoiceInfo()
    }
  }, [isOpen, ordersId, handleGetInvoiceInfo])

  const handleClose = () => {
    setInvoiceInfo(null)
    setError(null)
    setIsInitialLoad(true)
    setHasSuccessfulLoad(false)
    setFormData({
      orderItemIds: [ordersId],
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      invoiceType: 'FACTURA',
      operatorCode: 'FACO',
      invoiceDocumentFormat: 'pdf',
      invoiceDocument: ''
    })
    setSelectedFile(null)
    onClose()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type !== 'application/pdf') {
        toast.error('Solo se permiten archivos PDF')
        return
      }
      
      if (file.size > 5 * 1024 * 1024) { // 5MB
        toast.error('El archivo no puede ser mayor a 5MB')
        return
      }

      setSelectedFile(file)
      
      // Convertir a base64
      const reader = new FileReader()
      reader.onload = (e) => {
        const base64 = e.target?.result as string
        const base64Data = base64.split(',')[1] // Remover el prefijo data:application/pdf;base64,
        setFormData(prev => ({
          ...prev,
          invoiceDocument: base64Data
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const handleInputChange = (field: keyof FalabellaInvoiceUploadRequest, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpload = async () => {
    if (!formData.invoiceNumber.trim()) {
      toast.error('Número de factura es requerido')
      return
    }

    if (!formData.invoiceDocument.trim()) {
      toast.error('Debe seleccionar un archivo PDF')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await falabellaInvoiceService.uploadInvoice(formData)

      if (response.success) {
        toast.success('Factura subida exitosamente')
        handleClose()
      } else {
        setError(response.message || 'Error al subir la factura')
        toast.error(response.message || 'Error al subir la factura')
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Error al subir la factura'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <CloudArrowUpIcon className="h-6 w-6 text-indigo-600" />
              </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Subir Documento Tributario
              </h2>
              <p className="text-sm text-gray-500">
                Pedido: {orderNumber || ordersId}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Loading State */}
          {isLoading && isInitialLoad && (
            <div className="flex flex-col items-center justify-center py-12">
              <LoadingSpinner />
              <p className="mt-4 text-gray-600">Cargando información de facturas...</p>
            </div>
          )}

          {/* Action Button - Solo mostrar si no está cargando y no hubo carga exitosa */}
          {!isLoading && !hasSuccessfulLoad && (
            <div className="mb-6">
              <button
                onClick={handleGetInvoiceInfo}
                disabled={!ordersId.trim()}
                className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <DocumentTextIcon className="h-4 w-4" />
                Cargar Información de Facturas
              </button>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-sm font-medium text-red-800">Error</span>
                </div>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          {hasSuccessfulLoad && (
            <div className="space-y-6">
              {/* Información de la factura */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                  <span className="font-medium text-indigo-800">Información de Factura</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Número de Factura *
                    </label>
                    <input
                      type="text"
                      value={formData.invoiceNumber}
                      onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Ej: FAC-001-2024"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de Factura *
                    </label>
                    <input
                      type="date"
                      value={formData.invoiceDate}
                      onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Documento *
                    </label>
                    <select
                      value={formData.invoiceType}
                      onChange={(e) => handleInputChange('invoiceType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="FACTURA">FACTURA</option>
                      <option value="BOLETA">BOLETA</option>
                      <option value="NOTA_DE_CREDITO">NOTA DE CRÉDITO</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Código de Operador *
                    </label>
                    <select
                      value={formData.operatorCode}
                      onChange={(e) => handleInputChange('operatorCode', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="FACO">FACO (Colombia)</option>
 {/*                      <option value="FACL">FACL (Chile)</option>
                      <option value="FAPE">FAPE (Perú)</option> */}
                    </select>
                  </div>
                </div>
              </div>

              {/* Subida de archivo */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CloudArrowUpIcon className="h-5 w-5 text-gray-600" />
                  <span className="font-medium text-gray-800">Archivo PDF</span>
                </div>
                <span className="text-xs text-gray-500">
                  Solo archivos PDF, máximo 5MB
                </span>
              </div>
                
                <div className="space-y-3">
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  
                  {selectedFile && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          {selectedFile.name}
                        </span>
                        <span className="text-xs text-green-600">
                          ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Botones de acción */}
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isLoading || !formData.invoiceNumber.trim() || !formData.invoiceDocument.trim()}
                  className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <LoadingSpinner />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <CloudArrowUpIcon className="h-4 w-4" />
                      Subir Factura
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FalabellaInvoiceUploadModal
